import React, { useCallback } from 'react';
import { Box, Hint, HintText, Text } from '@common-fe/common-fe';

import Link from '@/modules/core/components/Link';

import { StatisticCard, StatisticDetailsIcon } from '../../StatisticCard.styles';
import { DelinquentClaimsStatisticTileConfig } from '../DelinquentClaimsStatistic.types';

import { StyledTitle } from './DelinquentClaimsStatistic.styles';
import DelinquentClaimsStatisticTile from './DelinquentClaimsStatisticTile';

export interface DelinquentClaimsStatisticProps {
  isLoading?: boolean;
  url: string;
  tiles: DelinquentClaimsStatisticTileConfig[];
  isDetailsLinkVisible?: boolean;
}

export const DelinquentClaimsStatistic: React.FC<DelinquentClaimsStatisticProps> = ({
  isLoading,
  tiles,
  url,
  isDetailsLinkVisible,
}) => {
  const getWrapper = useCallback(
    (node: React.ReactNode) => {
      if (isDetailsLinkVisible) {
        return (
          <Link testId="dashboard_statistics_delinquent-claims_details-link" isOpenNewTab to={url}>
            {node}
          </Link>
        );
      }
      return node;
    },
    [isDetailsLinkVisible, url]
  );

  return getWrapper(
    <StatisticCard
      data-testid="dashboard_statistics_delinquent-claims"
      background="canvas"
      border={{ color: 'border2', size: '1px' }}
      round="container1Round"
      pad="spacing16"
      direction="column"
      gap="spacing12"
      height={{ min: '140px' }}
    >
      <Box direction="row" align="center" justify="between" gap="spacing12" height="20px">
        <Hint
          hintWidth={320}
          hintHeight={100}
          hintElement={
            <Box>
              <HintText>
                <StyledTitle
                  data-testid="dashboard_statistics_delinquent-claims_title"
                  size="medium"
                  weight={700}
                >
                  Delinquent Claims
                </StyledTitle>
              </HintText>
            </Box>
          }
        >
          <Text
            data-testid="dashboard_statistics_delinquent-claims_title_hint"
            textAlign="center"
            size="small"
          >
            Card swipe claims that reached an escalation stage because the required documentation is still
            missing
          </Text>
        </Hint>

        {isDetailsLinkVisible && (
          <Box justify="center" align="center" width="24px" height="24px">
            <StatisticDetailsIcon
              data-testid="dashboard_statistics_delinquent-claims_details-icon"
              color="iconDisabled"
            />
          </Box>
        )}
      </Box>

      <Box direction="row" align="stretch" gap="spacing8">
        {tiles.map((tile) => (
          <DelinquentClaimsStatisticTile key={tile.type} {...tile} isLoading={isLoading} />
        ))}
      </Box>
    </StatisticCard>
  );
};

export default DelinquentClaimsStatistic;
